import {
    useRef,
    useState,
    useEffect,
    useCallback,
    useId,
    FC,
    Fragment,
    ReactNode,
    MouseEvent
} from "react";
import {
    useLocation,
    useNavigate,
    useSearchParams,
    useParams,
    Params,
    Link
} from "react-router-dom";
import { Menu, MenuItem, Modal, Popover, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import { ReactComponent as GridIcon } from "../../../assets/grid.icon.svg";
import { ReactComponent as DriveIcon } from "../../../assets/drive.icon.svg";
import { ReactComponent as PlusCircleIcon } from "../../../assets/plus-circle.icon.svg";
import { ReactComponent as GearIcon } from "../../../assets/gear.icon.svg";
import { ReactComponent as MoonIcon } from "../../../assets/moon.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refresh.icon.svg";
import { ReactComponent as SummaryIcon } from "../../../assets/summary.icon.svg";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ReactComponent as CloudErrorIcon } from "../../../assets/cloud--error.icon.svg";
import { ReactComponent as DiagramIcon } from "../../../assets/diagram.icon.svg";
import { ReactComponent as CubeIcon } from "../../../assets/cube.icon.svg";
import { forceUpdate } from "../../../api/admin/knowledge-base/endpoints-referrers";
import {
    isSharedContentItemProvider,
    refreshContentItemProvider,
    deleteContentItemProvider,
    setProviderVisibilityDefault,
    resetProviderVisibility,
    SetProviderVisibilityRequest
} from "../../../api/admin/content-providers/endpoints-referrers";
import { Logo } from "../../../common/enums/logo.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { UserRole } from "../../../common/enums/user-role.enum";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import ProviderVisibilityDialog from "../visibility-dialog/provider-visibility-dialog.comp";
import { AssistantEditDialog } from "../assistant-edit-dialog/assistant-edit-dialog.comp";
import { StatisticsContainer } from "../../../common/containers/statistics/statistics.container";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import { ThemeContainer } from "../../../common/containers/theme/theme.container";
import { ToastContainer } from "../../toast/toast.comp";
import {
    isMobileDevice,
    filterMobileSupported
} from "../../../common/utilities/platform/platform.util";
import { getAssistantIcon } from "../../../common/utilities/logo/logo.util";
import { visibleAdminNav } from "../../../common/utilities/routes/admin-nav.const";
import {
    KnowledgeBaseMenu,
    KnowledgeBaseMenuItem,
    KnowledgeBaseMenuItemType
} from "../../knowledge-base-menu/knowledge-base-menu.comp";
import {
    SettingItemType,
    SettingMenuType,
    SettingItem,
    SettingMenu
} from "../../dropdown-menu/dropdown-menu.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../confirmation-popup/confirmation-popup.comp";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { ProviderRefreshModal } from "../../modals/provider-refresh/provider-refresh-modal.comp";
import { ProviderPropertiesModal } from "../../modals/provider-properties/provider-properties-modal.comp";
import LinkGoogleDriveView from "../../../views/admin/link-google-drive/link-google-drive";
import TivaDataModalView from "../../../views/tiva-data/tiva-data";

enum MenuItemType {
    stroke = "stroke",
    fill = "fill",
    gradient = "gradient",
    default = "default"
}

interface LinkMenuItem {
    type: MenuItemType;
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    isAdmin: boolean;
    disabled: boolean;
    link?: string;
    action?: () => void;
    settings?: SettingItem[];
    submenuList?: SettingItem[];
    // When true, this item remains visible on mobile devices. See
    // filterMobileSupported in platform.util.
    mobileSupported?: boolean;
}

// Enter/Space activation for non-native-button controls that carry
// role="button" (e.g. <a> without href, or <div onClick>). Re-dispatches the
// element's own click handler so keyboard users can operate them (WCAG 2.1.1).
const clickOnKey = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
    }
};

const OptionalLink = ({
    children,
    item,
    className,
    onClick
}: {
    children: ReactNode;
    item: LinkMenuItem;
    className: string;
    onClick?: () => void;
}) => {
    const marginLeft = 6;
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleSubmenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(prevAnchor => (prevAnchor ? null : event.currentTarget));
    };

    const handleSubmenuClose = () => {
        setMenuAnchor(null);
    };

    // Activate a submenu item: close the menu (MUI Menu restores focus to the
    // trigger), then run the item's handler + the parent's onClick.
    const handleActionClick = (handler?: () => void) => {
        setMenuAnchor(null);
        if (handler) {
            handler();
        }
        if (onClick) {
            onClick();
        }
    };

    return item.submenuList && !item.disabled ? (
        <>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- hrefless submenu trigger exposed as role="button" with full keyboard support (Enter/Space via clickOnKey); kept as <a> to share the menu-item class styling that also covers the <Link> branch below. */}
            <a
                className={className}
                role="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={handleSubmenuClick}
                onKeyDown={clickOnKey}
            >
                {children}
            </a>
            <Menu
                id="dropdown-submenu"
                className="dropdown-submenu"
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleSubmenuClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-submenu-content",
                        style: { marginLeft: `${marginLeft}px` }
                    }
                }}
                MenuListProps={{ "aria-label": item.title, className: "dropdown-submenu-list" }}
            >
                {item.submenuList.map(setting => (
                    <MenuItem
                        key={setting.name}
                        className="dropdown-submenu-item"
                        onClick={() => handleActionClick(setting.handler)}
                    >
                        <span className="dropdown-submenu-item__text">{setting.name}</span>
                    </MenuItem>
                ))}
            </Menu>
        </>
    ) : item.link && !item.disabled ? (
        <Link
            className={className}
            to={item.link}
            onClick={() => {
                handleActionClick(item.action);
            }}
        >
            {children}
        </Link>
    ) : (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid -- hrefless menu action exposed as role="button" with full keyboard support; kept as <a> to share the menu-item class styling that also covers the <Link> branch above.
        <a
            className={className}
            role="button"
            tabIndex={item.disabled ? -1 : 0}
            aria-disabled={item.disabled || undefined}
            onClick={() => {
                if (!item.disabled) {
                    handleActionClick(item.action);
                }
            }}
            onKeyDown={clickOnKey}
        >
            {children}
        </a>
    );
};

const MenuLink = ({ item, isSelected = false }: { item: LinkMenuItem; isSelected?: boolean }) => {
    const { setLeftMenuShown } = ThemeContainer.useContainer();

    return (
        <div
            className={`general-menu-item general-menu-item--${item.type}${isSelected ? " general-menu-item--selected" : ""}${item.disabled ? " general-menu-item--disabled" : ""}`}
        >
            <OptionalLink
                item={item}
                onClick={() => {
                    setLeftMenuShown(false);
                }}
                className={`general-menu-item-link${item.settings ? " general-menu-item-link--more" : ""}`}
            >
                <SvgIcon
                    className="general-menu-item-link__icon"
                    component={item.icon}
                    inheritViewBox
                />
                <span className="general-menu-item-link__title">{item.title}</span>
            </OptionalLink>
            {item.settings && (
                <SettingMenu
                    className="general-menu-item-more"
                    type={SettingMenuType.more}
                    actions={item.settings}
                    ariaLabel={`Actions for ${item.title}`}
                />
            )}
        </div>
    );
};

const GeneralSubMenu = ({
    icon,
    title,
    actions,
    currentMenu,
    disabled = false,
    triggerRef
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    actions: LinkMenuItem[];
    currentMenu?: LinkMenuItem;
    disabled?: boolean;
    triggerRef?: React.Ref<HTMLAnchorElement>;
}) => {
    const { setLeftMenuShown } = ThemeContainer.useContainer();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);
    const triggerId = useId();

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = () => {
        handleMenuClose();
        setLeftMenuShown(false);
    };

    const isCurrentMenu = (item: LinkMenuItem) => {
        return !!currentMenu?.link && currentMenu.link === item.link;
    };

    return (
        <Fragment>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- hrefless popover trigger exposed as role="button" with full keyboard support (Enter/Space via clickOnKey); kept as <a> to preserve the general-submenu-item styling. */}
            <a
                ref={triggerRef}
                className={`general-submenu-item${disabled ? " general-submenu-item--disabled" : ""}`}
                role="button"
                tabIndex={disabled ? -1 : 0}
                id={triggerId}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                aria-disabled={disabled || undefined}
                onClick={!disabled ? handleMenuClick : undefined}
                onKeyDown={!disabled ? clickOnKey : undefined}
            >
                <SvgIcon className="general-submenu-item__icon" component={icon} inheritViewBox />
                <span className="general-submenu-item__title">{title}</span>
            </a>
            <Popover
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "general-dropdown-menu",
                        "aria-labelledby": triggerId
                    }
                }}
                transformOrigin={{ horizontal: "left", vertical: "center" }}
                anchorOrigin={{ horizontal: "right", vertical: "center" }}
            >
                <div className="general-dropdown-menu-list">
                    {actions.map(action => (
                        <div
                            key={action.title}
                            className={`general-dropdown-menu-item${isCurrentMenu(action) ? " general-dropdown-menu-item--selected" : ""}${action.disabled ? " general-dropdown-menu-item--disabled" : ""}`}
                        >
                            <OptionalLink
                                className="general-dropdown-menu-item-link"
                                item={action}
                                onClick={handleMenuItem}
                            >
                                <SvgIcon
                                    className="general-dropdown-menu-item-link__icon"
                                    component={action.icon}
                                    inheritViewBox
                                />
                                <span className="general-dropdown-menu-item-link__title">
                                    {action.title}
                                </span>
                            </OptionalLink>
                        </div>
                    ))}
                </div>
            </Popover>
        </Fragment>
    );
};

// Message for a failed provider action: the server's own text for a 409
// (an import is running for this source), the fallback otherwise.
const providerErrorMessage = (error: unknown, fallback: string): string => {
    const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
        ?.response;
    if (response?.status === 409 && typeof response.data?.message === "string") {
        return response.data.message;
    }
    return fallback;
};

const GeneralMenu = ({
    assistantAvailable = true,
    collapsedType = false,
    showForceSync = false
}: {
    assistantAvailable?: boolean;
    collapsedType?: boolean;
    showForceSync?: boolean;
}) => {
    const providerPathname = "knowledge-base";
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { providerId } = useParams<Params<string>>();
    const { showToast } = ToastContainer.useContainer();
    const { demoQuestions, isReady: isStatisticsReady, query } = StatisticsContainer.useContainer();
    const { providers, loadProviders } = ProvidersContainer.useContainer();
    const { logo, isLightTheme, changeTheme, setLoadingLoader } = ThemeContainer.useContainer();
    const {
        user,
        knowledgeBase,
        knowledgeBases,
        isSuperAdmin,
        isAdmin,
        isTrial,
        hasAdminFeature,
        updateUserKnowledgeBase,
        loadKnowledgeBases,
        refreshKnowledgeBase,
        loadKnowledgeBaseConfig
    } = FirebaseAuthContainer.useContainer();
    const [currentMenuItem, setCurrentMenuItem] = useState<LinkMenuItem>();
    const [mainLinkList, setMainLinkList] = useState<LinkMenuItem[]>([]);
    const [providerList, setProviderList] = useState<LinkMenuItem[]>([]);
    const [settingList, setSettingList] = useState<LinkMenuItem[]>([]);
    const [questionList, setQuestionList] = useState<SettingItem[]>([]);
    const [kbList, setKBList] = useState<KnowledgeBaseMenuItem[]>([]);
    const [refreshableProvider, setRefreshableProvider] = useState<Provider>();
    const [deletableProvider, setDeletableProvider] = useState<Provider>();
    const [propertiesProvider, setPropertiesProvider] = useState<Provider>();
    const [propertiesModalOpen, setPropertiesModalOpen] = useState<boolean>(false);
    const [visibilityProvider, setVisibilityProvider] = useState<Provider>();
    const [visibilityDialogOpen, setVisibilityDialogOpen] = useState<boolean>(false);
    // Edit the CURRENT Assistant in place (reuses the Assistants page dialog).
    const [assistantEditOpen, setAssistantEditOpen] = useState<boolean>(false);
    const [addSourceModalOpen, setAddSourceModalOpen] = useState<boolean>(false);
    const [refreshDriveModalOpen, setRefreshDriveModalOpen] = useState<boolean>(false);
    const [deleteDriveConfirmationOpen, setDeleteDriveConfirmationOpen] = useState<boolean>(false);
    const [tivaDataResultOpen, setTivaDataResultOpen] = useState<boolean>(false);
    // The analytics modal is opened from the Statistics submenu, which closes with
    // the Settings menu — so on modal close there's no submenu item left to return
    // to. Restore focus to the always-present Settings gear instead (WCAG 2.4.3).
    const settingsTriggerRef = useRef<HTMLAnchorElement>(null);
    const tivaWasOpenRef = useRef(false);
    const [forceSyncConfirmationOpen, setForceSyncConfirmationOpen] = useState<boolean>(false);
    const forceSyncDescription = `Use forced synchronization only if you urgently need to update the knowledge base.
    Otherwise, automatic sync will still occur as scheduled.`;

    const isTrialExpired = useCallback(() => {
        return (
            isTrial &&
            knowledgeBase?.expiringTrialPeriod != null &&
            knowledgeBase?.expiringTrialPeriod < 1
        );
    }, [isTrial, knowledgeBase]);

    const handleAddSourceClick = useCallback(() => {
        if (!isMobileDevice()) {
            handleAddSourceModalOpen();
        }
    }, []);

    const handleAddSourceModalOpen = () => {
        setAddSourceModalOpen(true);
    };

    const handleAddSourceModalClose = () => {
        setAddSourceModalOpen(false);
    };

    const handleAddSourceSuccess = () => {
        loadProviders();
    };

    const handleForceSyncClick = useCallback(() => {
        if (!isMobileDevice()) {
            handleForceSyncConfirmationOpen();
        }
    }, []);

    const handleForceSyncConfirmationOpen = () => {
        setForceSyncConfirmationOpen(true);
    };

    const handleForceSyncConfirmationClose = () => {
        setForceSyncConfirmationOpen(false);
    };

    const handleForceSyncConfirmationAction = () => {
        if (!user?.currentKnowledgeBaseId) return;

        forceUpdate(user.companyId, user.currentKnowledgeBaseId);
        handleForceSyncConfirmationClose();
    };

    const handleChangeTheme = useCallback(() => {
        changeTheme();
    }, [changeTheme]);

    const handleRefreshSourceModalOpen = () => {
        setRefreshDriveModalOpen(true);
    };

    const handleRefreshSourceModalClose = () => {
        setRefreshDriveModalOpen(false);
        setRefreshableProvider(undefined);
    };

    const handleRefreshSourceSuccess = useCallback(() => {
        loadProviders();
    }, [loadProviders]);

    const handleRefreshSource = useCallback(
        async (provider: Provider) => {
            if (!user?.currentKnowledgeBaseId) return;

            // The server rejects a refresh with 409 while an import is in its
            // scan/create phases. It reads the live status; the provider list
            // held here is only reloaded on actions, so it is not consulted.
            if (provider.name === ProviderType.googleDrive) {
                setLoadingLoader(true);
                try {
                    const isShared = (
                        await isSharedContentItemProvider(user.companyId, provider.id)
                    ).data.isPublic;

                    if (isShared) {
                        await refreshContentItemProvider(user.companyId, provider.id, {
                            knowledgeBaseId: user.currentKnowledgeBaseId
                        });
                        handleRefreshSourceSuccess();
                    } else {
                        setRefreshableProvider(provider);
                        handleRefreshSourceModalOpen();
                    }
                } catch (error) {
                    showToast(providerErrorMessage(error, "Could not refresh the source"));
                } finally {
                    setLoadingLoader(false);
                }
            } else if (provider.name === ProviderType.oneDrive) {
                setRefreshableProvider(provider);
                handleRefreshSourceModalOpen();
            } else if (provider.name === ProviderType.webScraper) {
                // Web scraper (web_scraper_provider_design.md §8.5): no token;
                // the server answers 409 while a crawl is awaiting review and
                // that message ("review it, or discard it…") is shown as-is.
                setLoadingLoader(true);
                try {
                    await refreshContentItemProvider(user.companyId, provider.id, {
                        knowledgeBaseId: user.currentKnowledgeBaseId
                    });
                    showToast("Crawl started. It stops for your review when it finishes.");
                    handleRefreshSourceSuccess();
                } catch (error) {
                    showToast(providerErrorMessage(error, "Could not start the crawl"));
                } finally {
                    setLoadingLoader(false);
                }
            }
        },
        [user, setLoadingLoader, handleRefreshSourceSuccess, showToast]
    );

    const handleSaveProviderVisibility = (values: SetProviderVisibilityRequest) => {
        if (!user || !visibilityProvider) return;
        setVisibilityDialogOpen(false);
        setProviderVisibilityDefault(user.companyId, visibilityProvider.id, values)
            .then(() => {
                loadProviders();
                showToast("Default visibility saved");
            })
            .catch(() => showToast("Something went wrong. Please try again later"));
    };

    const handleResetProviderVisibility = (values: SetProviderVisibilityRequest) => {
        if (!user || !visibilityProvider) return;
        setVisibilityDialogOpen(false);
        resetProviderVisibility(user.companyId, visibilityProvider.id, values)
            .then(response => {
                loadProviders();
                showToast(`Reset ${response.data.updatedFiles} file(s) to the default`);
            })
            .catch(() => showToast("Something went wrong. Please try again later"));
    };

    const openTivaDataResult = useCallback(
        async (cubeQuery: any, questionText: string) => {
            if (!isStatisticsReady) {
                showToast(`Statistics service is not ready. Please try again.`);
                return;
            }
            setLoadingLoader(true);
            try {
                const result = await query(cubeQuery);
                setLoadingLoader(false);
                if (result) {
                    // Build payload that matches the expected format for tiva-data view
                    const payload = {
                        query: cubeQuery,
                        queryText: questionText,
                        measures: cubeQuery.measures || [],
                        propertiesToShow: cubeQuery.dimensions || [],
                        timeDimensions: cubeQuery.timeDimensions || [],
                        groups: [],
                        measureFilters: { operator: "and", members: [] },
                        dimensionFilters: {
                            operator: "and",
                            members: cubeQuery.filters || []
                        },
                        modelType: { id: "question_answer_log", name: "Question Answer Log" }
                    };
                    navigate(`${location.pathname}?tivaDataPayload=${JSON.stringify(payload)}`);
                } else {
                    showToast("Something went wrong. Please try again later");
                }
            } catch (error) {
                setLoadingLoader(false);
                showToast("Something went wrong. Please try again later");
            }
        },
        [isStatisticsReady, location.pathname, query, navigate, showToast, setLoadingLoader]
    );

    const closeTivaDataResult = () => {
        navigate(location.pathname);
    };

    useEffect(() => {
        setTivaDataResultOpen(!!searchParams.get("tivaDataPayload"));
    }, [searchParams]);

    // When the analytics modal transitions from open → closed, move focus to the
    // Settings gear. Done in an effect (after the modal has unmounted) and deferred
    // a frame so it's a fresh focus event VoiceOver's cursor follows (WCAG 2.4.3);
    // the Modal has disableRestoreFocus so it won't fight this.
    useEffect(() => {
        if (tivaWasOpenRef.current && !tivaDataResultOpen) {
            requestAnimationFrame(() => settingsTriggerRef.current?.focus());
        }
        tivaWasOpenRef.current = tivaDataResultOpen;
    }, [tivaDataResultOpen]);

    useEffect(() => {
        if (!demoQuestions?.length) return;

        setQuestionList(
            demoQuestions.map(item => ({
                name: item.question,
                type: SettingItemType.default,
                handler: () => {
                    openTivaDataResult(item.query, item.question);
                }
            }))
        );
    }, [demoQuestions, openTivaDataResult]);

    // No "All assistants" entry here (unlike the chat sidebar): this picker
    // selects which Assistant is being ADMINISTERED — every admin view is
    // scoped to one KB, and the orchestrator is not an administrable entity.
    // The chat-routing selection lives in chat-list.comp only.
    useEffect(() => {
        const items: KnowledgeBaseMenuItem[] = knowledgeBases.map(kb => ({
            type: KnowledgeBaseMenuItemType.stroke,
            icon: CubeIcon,
            title: kb.displayName,
            role: kb.role,
            kbId: kb.uid,
            logoUrl: kb.logoUrl,
            action: () => {
                setLoadingLoader(true);
                updateUserKnowledgeBase(kb.uid).then(() => {
                    navigate(kb.role === "user" ? "/new-chat" : "/");
                    setLoadingLoader(false);
                });
            }
        }));
        setKBList(items);
    }, [navigate, knowledgeBases, updateUserKnowledgeBase, setLoadingLoader]);

    useEffect(() => {
        const list: LinkMenuItem[] = [
            {
                type: MenuItemType.stroke,
                icon: GridIcon,
                title: "Dashboard",
                isAdmin: true,
                disabled: isTrialExpired(),
                link: "/dashboard"
            }
        ];
        if (isAdmin) {
            list.push({
                type: logo === Logo.mba ? MenuItemType.default : MenuItemType.fill,
                icon: getAssistantIcon(logo),
                title: "Chat",
                isAdmin: false,
                disabled: !assistantAvailable || isTrialExpired(),
                link: "/new-chat",
                mobileSupported: true
            });
        }
        setMainLinkList(filterMobileSupported(list));
    }, [logo, isAdmin, assistantAvailable, isTrialExpired]);

    useEffect(() => {
        // Phase C: the company-scoped route entries derive from ADMIN_NAV so
        // gear menu, Dashboard tiles, and page titles can never drift. The
        // gates mirror the server guards (§8.2): isAdmin also covers per-KB
        // managers, who reach only the manager-gated entries (D8).
        const list: LinkMenuItem[] = visibleAdminNav({
            canManage: !!isAdmin,
            isCompanyAdmin: !!isSuperAdmin || user?.role === UserRole.admin,
            isSuperAdmin: !!isSuperAdmin,
            hasAdminFeature
        }).map(entry => ({
            type: MenuItemType.stroke,
            icon: entry.icon,
            title: entry.title,
            isAdmin: true,
            disabled: false,
            link: entry.path
        }));
        // Statistics: the tenant permission withholds it from company admins;
        // managers keep it (permissions never subtract from managers).
        const statisticsVisible =
            isSuperAdmin || user?.role === UserRole.admin ? hasAdminFeature("statistics") : true;
        if (isAdmin && statisticsVisible) {
            list.push({
                type: MenuItemType.stroke,
                icon: DiagramIcon,
                title: "Statistics",
                isAdmin: true,
                disabled: !isStatisticsReady,
                submenuList: questionList
            });
        }
        list.push({
            type: MenuItemType.stroke,
            icon: MoonIcon,
            title: isLightTheme ? "Dark theme" : "Light theme",
            isAdmin: true,
            disabled: false,
            action: handleChangeTheme,
            mobileSupported: true
        });
        if (showForceSync) {
            list.push({
                type: MenuItemType.stroke,
                icon: RefreshIcon,
                title: "Force Sync",
                isAdmin: true,
                disabled: false,
                action: handleForceSyncClick
            });
        }
        setSettingList(filterMobileSupported(list));
    }, [
        questionList,
        isStatisticsReady,
        isLightTheme,
        isTrial,
        isAdmin,
        isSuperAdmin,
        hasAdminFeature,
        knowledgeBases,
        showForceSync,
        handleForceSyncClick,
        handleAddSourceClick,
        handleChangeTheme,
        user?.role
    ]);

    useEffect(() => {
        if (isTrial) return;

        const list = providers
            .sort((a, b) => a.id - b.id)
            .map(provider => ({
                type: MenuItemType.stroke,
                icon: DriveIcon,
                title:
                    provider.name === ProviderType.fileUploader ? "My Files" : provider.displayName,
                isAdmin: true,
                disabled: false,
                hasActions: true,
                link: `/${providerPathname}/${provider.id}`,
                settings:
                    provider.name === ProviderType.googleDrive ||
                    provider.name === ProviderType.oneDrive ||
                    provider.name === ProviderType.webScraper
                        ? [
                              {
                                  icon: RefreshIcon,
                                  name: "Refresh",
                                  type: SettingItemType.default,
                                  handler: () => {
                                      handleRefreshSource(provider);
                                  }
                              },
                              {
                                  icon: SummaryIcon,
                                  name: "Properties",
                                  type: SettingItemType.default,
                                  handler: () => {
                                      setPropertiesProvider(provider);
                                      setPropertiesModalOpen(true);
                                  }
                              },
                              {
                                  icon: EyeIcon,
                                  name: "Visibility",
                                  type: SettingItemType.default,
                                  handler: () => {
                                      setVisibilityProvider(provider);
                                      setVisibilityDialogOpen(true);
                                  }
                              },
                              {
                                  icon: TrashIcon,
                                  name: "Delete",
                                  type: SettingItemType.default,
                                  handler: () => {
                                      setDeletableProvider(provider);
                                      setDeleteDriveConfirmationOpen(true);
                                  }
                              }
                          ]
                        : [
                              {
                                  icon: EyeIcon,
                                  name: "Visibility",
                                  type: SettingItemType.default,
                                  handler: () => {
                                      setVisibilityProvider(provider);
                                      setVisibilityDialogOpen(true);
                                  }
                              }
                          ]
            }));
        setProviderList(filterMobileSupported<LinkMenuItem>(list));
        if (isAdmin) {
            setProviderList(prevList =>
                filterMobileSupported<LinkMenuItem>([
                    ...prevList,
                    {
                        type: MenuItemType.stroke,
                        icon: PlusCircleIcon,
                        title: "Add Source",
                        isAdmin: true,
                        disabled: false,
                        action: handleAddSourceClick
                    }
                ])
            );
        }
    }, [isAdmin, isTrial, providers, handleAddSourceClick, handleRefreshSource]);

    useEffect(() => {
        if (!isTrial) return;

        setProviderList(
            filterMobileSupported<LinkMenuItem>(
                isAdmin
                    ? [
                          {
                              type: MenuItemType.stroke,
                              icon: DriveIcon,
                              title: "My Files",
                              isAdmin: true,
                              disabled: isTrialExpired(),
                              link: "/knowledge-base"
                          },
                          {
                              type: MenuItemType.stroke,
                              icon: PlusCircleIcon,
                              title: "Add Source (Upgrade)",
                              isAdmin: true,
                              disabled: true
                          }
                      ]
                    : []
            )
        );
    }, [isTrial, isAdmin, isTrialExpired]);

    useEffect(() => {
        const menuItem = location.pathname.includes(providerPathname)
            ? providerList.find(item =>
                  isTrial
                      ? location.pathname === item.link
                      : `/${providerPathname}/${providerId}` === item.link
              )
            : (mainLinkList.find(item => location.pathname === item.link) ??
              settingList.find(item => location.pathname === item.link));
        setCurrentMenuItem(menuItem);
    }, [location, isTrial, providerId, providerList, mainLinkList, settingList]);

    const isCurrentMenu = (item: LinkMenuItem) => {
        return !!currentMenuItem?.link && currentMenuItem.link === item.link;
    };

    const handleDeleteDriveConfirmationAction = () => {
        if (!user || !deletableProvider) return;

        deleteContentItemProvider(user.companyId, deletableProvider.id)
            .then(() => {
                loadProviders();

                if (Number(providerId) === deletableProvider.id) {
                    navigate(`/knowledge-base`);
                }
            })
            .catch(error => {
                // 409 while an import is scanning/creating; the server message
                // says so. Reload so the list reflects the live status.
                showToast(providerErrorMessage(error, "Could not delete the source"));
                loadProviders();
            });
        setDeleteDriveConfirmationOpen(false);
    };

    return (
        <div className={`general-menu${collapsedType ? " general-menu--collapsed" : ""}`}>
            <div className="general-menu-items general-menu-items--fixed">
                {kbList.length > 0 && (
                    <KnowledgeBaseMenu
                        className="general-menu-kb"
                        items={kbList}
                        currentKnowledgeBaseId={user?.currentKnowledgeBaseId}
                        collapsed={collapsedType}
                    />
                )}
                {mainLinkList.map(item => (
                    <Fragment key={item.link ?? item.title}>
                        <MenuLink item={item} isSelected={isCurrentMenu(item)} />
                    </Fragment>
                ))}
                {!!providerList.length && <div className="general-menu-separator"></div>}
            </div>
            <div className="general-menu-items">
                {isAdmin && !!knowledgeBase && (
                    <MenuLink
                        item={{
                            type: MenuItemType.stroke,
                            icon: GearIcon,
                            title: "Assistant Settings",
                            isAdmin: true,
                            disabled: false,
                            action: () => setAssistantEditOpen(true)
                        }}
                        isSelected={false}
                    />
                )}
                {providerList.map(item => (
                    <Fragment key={item.link ?? item.title}>
                        <MenuLink item={item} isSelected={isCurrentMenu(item)} />
                    </Fragment>
                ))}
            </div>
            <div className="general-submenu">
                <GeneralSubMenu
                    icon={GearIcon}
                    title="Settings"
                    actions={settingList}
                    currentMenu={currentMenuItem}
                    disabled={isTrialExpired()}
                    triggerRef={settingsTriggerRef}
                />
            </div>
            {deletableProvider && (
                <ConfirmationPopup
                    open={deleteDriveConfirmationOpen}
                    type={ConfirmationPopupType.dangerous}
                    icon={CloudErrorIcon}
                    title="Disconnect Drive"
                    description={`Disconnecting "${deletableProvider.displayName}" will remove access to all the data associated with it.
                                MBAssist won't be able to answer questions on those topics.
                                This can't be undone!`}
                    actionName="Disconnect"
                    handleAction={handleDeleteDriveConfirmationAction}
                    handleClose={() => setDeleteDriveConfirmationOpen(false)}
                />
            )}
            {refreshableProvider && (
                <ProviderRefreshModal
                    open={refreshDriveModalOpen}
                    provider={refreshableProvider}
                    handleClose={handleRefreshSourceModalClose}
                    handleAction={handleRefreshSourceSuccess}
                />
            )}
            {propertiesProvider && (
                <ProviderPropertiesModal
                    open={propertiesModalOpen}
                    provider={propertiesProvider}
                    handleClose={() => {
                        setPropertiesModalOpen(false);
                        setPropertiesProvider(undefined);
                    }}
                />
            )}
            {assistantEditOpen && !!knowledgeBase && !!user?.companyId && (
                <AssistantEditDialog
                    companyId={user.companyId}
                    assistant={knowledgeBase}
                    features={{
                        prompt: hasAdminFeature("assistantPrompt"),
                        retrieval: hasAdminFeature("assistantRetrieval"),
                        recognition: hasAdminFeature("processingRecognition"),
                        chunking: hasAdminFeature("processingChunking"),
                        metadata: hasAdminFeature("processingMetadata"),
                        maintenance: hasAdminFeature("processingMaintenance"),
                        // Mirrors the Assistants page: the embedding-model
                        // change keeps its platform super-admin gate.
                        embedding: !!isSuperAdmin,
                        invites: hasAdminFeature("inviteLinks")
                    }}
                    initialTab={null}
                    onClose={() => setAssistantEditOpen(false)}
                    onSaved={() => {
                        setAssistantEditOpen(false);
                        // This is always the CURRENT Assistant — refresh the
                        // workspace the same way the Assistants page does.
                        refreshKnowledgeBase();
                        if (user?.companyId) {
                            loadKnowledgeBases(user.companyId);
                            loadKnowledgeBaseConfig(user.companyId, knowledgeBase.uid);
                        }
                    }}
                />
            )}
            {visibilityProvider && (
                <ProviderVisibilityDialog
                    open={visibilityDialogOpen}
                    onClose={() => {
                        setVisibilityDialogOpen(false);
                        setVisibilityProvider(undefined);
                    }}
                    providerName={
                        visibilityProvider.name === ProviderType.fileUploader
                            ? "My Files"
                            : visibilityProvider.displayName
                    }
                    showLiveLink={
                        visibilityProvider.name === ProviderType.googleDrive ||
                        visibilityProvider.name === ProviderType.oneDrive
                    }
                    webSite={visibilityProvider.name === ProviderType.webScraper}
                    initialCitationDisplay={visibilityProvider.defaultCitationDisplay}
                    initialCitationLiveLink={visibilityProvider.citationLiveLink}
                    onSaveDefault={handleSaveProviderVisibility}
                    onResetAll={handleResetProviderVisibility}
                />
            )}
            <ConfirmationPopup
                open={forceSyncConfirmationOpen}
                icon={RefreshIcon}
                title="Force Sync"
                description={forceSyncDescription}
                actionName="Proceed"
                handleAction={handleForceSyncConfirmationAction}
                handleClose={handleForceSyncConfirmationClose}
            />
            <ModalTemplate
                open={addSourceModalOpen}
                width={654}
                handleClose={handleAddSourceModalClose}
                ariaLabel="Add Source"
            >
                <LinkGoogleDriveView
                    handleClose={handleAddSourceModalClose}
                    handleSuccess={handleAddSourceSuccess}
                />
            </ModalTemplate>
            <Modal
                className="tiva-data-modal"
                open={tivaDataResultOpen}
                onClose={closeTivaDataResult}
                disableRestoreFocus
            >
                <div
                    className="tiva-data-container"
                    role="dialog"
                    aria-modal="true"
                    aria-label="TIVA data"
                >
                    <TivaDataModalView handleClose={closeTivaDataResult} />
                </div>
            </Modal>
        </div>
    );
};

export default GeneralMenu;
